/* import __COLOCATED_TEMPLATE__ from './submenu-nav.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type AiAgentSetupService from 'embercom/services/ai-agent-setup-service';
import { canSeeCustomAnswers } from 'embercom/helpers/can-see-custom-answers';
import RouteRegexes from 'embercom/lib/route-regexes';
import type GreatGuidanceService from 'embercom/services/great-guidance-service';
import type { FinGettingStartedStates } from 'embercom/lib/reporting/fin-getting-started-states';
import type FinAgentTypeService from 'embercom/services/fin-agent-type-service';
import type ReactTeammateAppIslandService from 'embercom/services/react-teammate-app-island';
import { get } from 'embercom/lib/ajax';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';

interface MenuItem {
  title: string;
  icon?: InterfaceIconName;
  url?: string;
  items?: MenuItem[];
  shouldHide?: boolean;
  stamp?: string;
  count?: number;
  external?: boolean;
  isSeparator?: boolean;
  status?: string;
  queryParams?: Record<string, any>;
  gettingStartedState?: FinGettingStartedStates;
  isDisabled?: boolean;
  disabledTooltipText?: string;
}

const BATCH_TEST_ROUTE = 'apps.app.automation.fin-ai-agent.testing.index';
const SIMULATIONS_ROUTE = 'apps.app.automation.fin-ai-agent.simulations';

const SHARED_ROUTE_PREFIXES = [
  'apps.app.automation.fin-ai-agent.settings',
  'apps.app.automation.fin-ai-agent.all-roles',
  'apps.app.automation.workflows-overview',
];

const FIN_AI_AGENT_ROUTE_PREFIX = 'apps.app.automation.fin-ai-agent';

const MENU_STRUCTURE = (
  app: any,
  profileCount: number,
  customAnswersCount: number,
  contentSuggestionsCount: number,
  chatStatus: string,
  emailStatus: string,
  phoneStatus: string,
  apiStatus: string,
  canSeeCustomAnswers: boolean,
  finGettingStartedState: FinGettingStartedStates,
  hideSalesItems: boolean,
): MenuItem[] => {
  let showProcedures = app.canUseFinProcedures || !app.canUseAiAgentWorkflows;
  let shouldHideGetStarted = !finGettingStartedState;
  return [
    {
      title: 'ai-agent.new-submenu.get-started',
      icon: 'tooltip',
      url: 'apps.app.automation.fin-ai-agent.analyze.get-started',
      shouldHide: shouldHideGetStarted,
    },
    {
      title: '',
      isSeparator: true,
      shouldHide: shouldHideGetStarted,
    },
    {
      title: 'ai-agent.new-submenu.ask',
      icon: 'fin',
      url: 'apps.app.automation.fin-ai-agent.ask.index',
      shouldHide: !app.canUseFeature('team-rec-internal-agent-combined'),
    },
    {
      title: 'ai-agent.new-submenu.train',
      icon: 'article',
      items: [
        {
          title: 'ai-agent.new-submenu.content',
          url: 'apps.app.automation.fin-ai-agent.content',
          queryParams: { native_content: false },
        },
        {
          title: 'ai-agent.new-submenu.guidance',
          url: 'apps.app.automation.fin-ai-agent.guidance',
          shouldHide: !app.canUseFinGuidance,
        },
        {
          title: 'ai-agent.new-submenu.guidance-v2',
          url: 'apps.app.automation.fin-ai-agent.guidance-v2',
          shouldHide: !app.canUseFeature('react-guidance-page'),
        },
        {
          title: 'ai-agent.new-submenu.ai-categories',
          url: 'apps.app.automation.fin-ai-agent.attributes',
          shouldHide: !app.canUseFinFirstAiCategories,
        },
        {
          title: 'ai-agent.new-submenu.escalation',
          url: 'apps.app.automation.fin-ai-agent.escalation',
          shouldHide: !app.canUseFeature('fin-escalation-rules'),
        },
        {
          title: showProcedures
            ? 'fin-ai-agent.train.procedures.title'
            : 'operator.sub-menu.fin-ai-agent.tasks',
          url: showProcedures
            ? 'apps.app.automation.fin-ai-agent.procedures.index'
            : 'apps.app.automation.fin-ai-agent.tasks.index',
        },
        {
          title: 'ai-agent.new-submenu.custom-answers',
          url: 'apps.app.automation.fin-ai-agent.custom-answers.index',
          count: customAnswersCount,
          shouldHide: !canSeeCustomAnswers,
        },
        {
          title: app.canUseFeature('team-self-serve-ai-insights-addon')
            ? 'ai-agent.new-submenu.content-recommendations'
            : 'ai-agent.new-submenu.content-suggestions',
          url: 'apps.app.automation.fin-ai-agent.content-suggestions',
          count: contentSuggestionsCount,
          shouldHide: !app.canSeeLegacyContentSuggestions || app.canUseSuggestions,
        },
        {
          title: 'ai-agent.new-submenu.sales-development',
          url: 'apps.app.automation.fin-ai-agent.sales-development',
          shouldHide: !app.canUseFinSalesAgent || hideSalesItems,
        },
      ],
    },
    {
      title: 'ai-agent.new-submenu.test',
      icon: 'test',
      url: app.canUseSophisticatedTestingCentralisedPage ? undefined : BATCH_TEST_ROUTE,
      items: app.canUseSophisticatedTestingCentralisedPage
        ? [
            {
              title: 'ai-agent.new-submenu.batch-test',
              url: BATCH_TEST_ROUTE,
            },
            {
              title: 'ai-agent.new-submenu.simulations',
              url: SIMULATIONS_ROUTE,
            },
          ]
        : undefined,
      shouldHide: !app.canUseFinPlayground,
    },
    {
      title: 'ai-agent.new-submenu.deploy',
      icon: 'rocket-ship',
      items: [
        {
          title: 'ai-agent.new-submenu.chat',
          url: 'apps.app.automation.fin-ai-agent.deploy.chat',
          status: chatStatus,
        },
        {
          title: 'ai-agent.new-submenu.email',
          url: 'apps.app.automation.fin-ai-agent.deploy.email',
          status: emailStatus,
        },
        {
          title: 'ai-agent.new-submenu.phone',
          url: 'apps.app.automation.fin-ai-agent.deploy.phone',
          status: phoneStatus,
        },
        {
          title: 'ai-agent.new-submenu.api',
          url: 'apps.app.automation.fin-ai-agent.deploy.api',
          shouldHide: !app.canUseFinAgentApi,
          status: apiStatus,
        },
        {
          title: 'ai-agent.new-submenu.profiles',
          url: 'apps.app.automation.fin-ai-agent.profiles.index',
          count: profileCount,
          shouldHide: profileCount === 0,
        },
      ],
    },
    {
      title: 'ai-agent.new-submenu.analyze',
      icon: 'column-chart',
      items: [
        {
          title:
            app.canUseFeature('fin-sales-agent') && !hideSalesItems
              ? 'ai-agent.new-submenu.support-performance'
              : 'ai-agent.new-submenu.performance',
          url: 'apps.app.automation.fin-ai-agent.analyze.performance',
        },
        {
          title: 'ai-agent.new-submenu.sales-performance',
          url: 'apps.app.automation.fin-ai-agent.analyze.sales-performance',
          shouldHide: !app.canUseFeature('fin-sales-agent') || hideSalesItems,
        },
        {
          title: 'ai-agent.new-submenu.leads',
          url: 'apps.app.automation.fin-ai-agent.analyze.leads',
          shouldHide: !app.canUseFeature('fin-sales-agent') || hideSalesItems,
        },
        {
          title: 'ai-agent.new-submenu.ai-insights',
          url: 'apps.app.automation.fin-ai-agent.analyze.ai-insights',
          shouldHide: app.canSeeAiTopics,
        },
        {
          title: app.canUseFeature('team-self-serve-ai-insights-addon')
            ? 'ai-agent.new-submenu.recommendations'
            : 'ai-agent.new-submenu.optimize-fin',
          url: 'apps.app.automation.fin-ai-agent.analyze.optimize-suggestions',
          shouldHide: !app.canSeeAiTopics,
        },
        {
          title: 'ai-agent.new-submenu.topics-explorer',
          url: 'apps.app.automation.fin-ai-agent.analyze.topics-explorer',
          shouldHide: !app.canSeeAiTopics,
        },
        {
          title: 'ai-agent.new-submenu.topic-trends',
          url: 'apps.app.automation.fin-ai-agent.analyze.topic-trends',
          shouldHide: !app.canSeeAiTopics,
        },
        {
          title: 'ai-agent.new-submenu.conversations',
          external: true,
          url: 'inbox.workspace.inbox.fin-view-redirect',
          shouldHide: app.canSeeMonitor,
        },
        {
          title: 'ai-agent.new-submenu.unresolved-questions',
          url: 'apps.app.automation.fin-ai-agent.analyze.unresolved-questions',
          shouldHide: app.canSeeAiTopics,
        },
        {
          title: 'ai-agent.new-submenu.monitor',
          url: 'apps.app.automation.fin-ai-agent.analyze.monitor',
          shouldHide: !app.canSeeMonitor,
        },
      ],
    },
    {
      title: 'ai-agent.new-submenu.settings',
      url: 'apps.app.automation.fin-ai-agent.settings.index',
      icon: 'settings',
      shouldHide: app.canUseMultiBrandGA,
    },
    {
      title: 'ai-agent.new-submenu.changelog',
      url: 'apps.app.automation.fin-ai-agent.changelog',
      icon: 'changelog',
    },
    {
      title: '',
      isSeparator: true,
    },
    {
      title: 'ai-agent.new-submenu.settings',
      icon: 'settings',
      items: [
        {
          title: 'ai-agent.sub-menu.settings.general',
          url: 'apps.app.automation.fin-ai-agent.settings.index',
        },
        {
          title: 'ai-agent.sub-menu.settings.audiences',
          url: 'apps.app.automation.fin-ai-agent.settings.audiences',
        },
        {
          title: 'ai-agent.sub-menu.settings.pronunciations',
          url: 'apps.app.automation.fin-ai-agent.settings.pronunciations',
          shouldHide: !app.canSetupFinVoicePhoneEasySetup,
        },
      ],
      shouldHide: !app.canUseMultiBrandGA,
    },
    {
      title: 'ai-agent.new-submenu.workflows',
      icon: 'workflows',
      url: 'apps.app.automation.workflows-overview',
    },
    {
      title: 'ai-agent.new-submenu.simple-automations',
      url: 'apps.app.automation.basics',
      icon: 'trigger',
    },
  ];
};

const SALES_MENU_STRUCTURE = (
  app: any,
  hasSdrConfig: boolean,
  canUseFinSalesAgent: boolean,
): MenuItem[] => {
  let isDisabled = !canUseFinSalesAgent;
  let disabledTooltipText = 'ai-agent.sales-submenu.disabled-tooltip';

  return [
    {
      title: 'ai-agent.new-submenu.get-started',
      icon: 'tooltip',
      url: 'apps.app.automation.fin-ai-agent.sales.get-started',
    },
    {
      title: '',
      isSeparator: true,
    },
    {
      title: 'ai-agent.new-submenu.ask',
      icon: 'fin',
      url: 'apps.app.automation.fin-ai-agent.sales.ask',
      shouldHide: !app.canUseFeature('sdr-ai-configurator'),
    },
    {
      title: 'ai-agent.new-submenu.train',
      icon: 'article',
      isDisabled,
      disabledTooltipText,
      items: [
        {
          title: 'ai-agent.sales-submenu.playbook',
          url: 'apps.app.automation.fin-ai-agent.sales.playbook',
        },
        {
          title: 'ai-agent.new-submenu.content',
          url: 'apps.app.automation.fin-ai-agent.sales.content',
        },
        {
          title: 'ai-agent.new-submenu.guidance',
          url: 'apps.app.automation.fin-ai-agent.sales.guidance',
          shouldHide: !hasSdrConfig,
        },
        {
          title: 'ai-agent.sales-submenu.integrations',
          url: 'apps.app.automation.fin-ai-agent.sales.integrations',
          shouldHide: !hasSdrConfig,
        },
      ],
    },
    {
      title: 'ai-agent.new-submenu.deploy',
      icon: 'rocket-ship',
      isDisabled,
      disabledTooltipText,
      items: [
        {
          title: 'ai-agent.new-submenu.chat',
          url: 'apps.app.automation.fin-ai-agent.sales.deploy.chat',
        },
        {
          title: 'ai-agent.new-submenu.email',
          url: 'apps.app.automation.fin-ai-agent.sales.deploy.email',
        },
      ],
    },
    {
      title: 'ai-agent.new-submenu.analyze',
      icon: 'column-chart',
      isDisabled,
      disabledTooltipText,
      items: [
        {
          title: 'ai-agent.new-submenu.performance',
          url: 'apps.app.automation.fin-ai-agent.sales.performance',
        },
        {
          title: 'ai-agent.sales-submenu.leads',
          url: 'apps.app.automation.fin-ai-agent.sales.leads',
          shouldHide: !app.canUseFeature('fin-sales-agent'),
        },
        {
          title: 'ai-agent.new-submenu.conversations',
          external: true,
          url: 'inbox.workspace.inbox.fin-sales-view-redirect',
        },
      ],
    },
    {
      title: 'ai-agent.new-submenu.settings',
      url: 'apps.app.automation.fin-ai-agent.settings.index',
      icon: 'settings',
      shouldHide: app.canUseMultiBrandGA,
    },
    {
      title: '',
      isSeparator: true,
    },
    {
      title: 'ai-agent.new-submenu.settings',
      icon: 'settings',
      items: [
        {
          title: 'ai-agent.sub-menu.settings.general',
          url: 'apps.app.automation.fin-ai-agent.settings.index',
        },
        {
          title: 'ai-agent.sub-menu.settings.audiences',
          url: 'apps.app.automation.fin-ai-agent.settings.audiences',
        },
      ],
      shouldHide: !app.canUseMultiBrandGA,
    },
    {
      title: 'ai-agent.new-submenu.workflows',
      icon: 'workflows',
      url: 'apps.app.automation.workflows-overview',
    },
  ];
};

const ALL_ROLES_MENU_STRUCTURE = (): MenuItem[] => {
  let disabledTooltipText = 'ai-agent.all-roles-submenu.disabled-tooltip';
  return [
    {
      title: 'ai-agent.new-submenu.train',
      icon: 'article',
      isDisabled: true,
      disabledTooltipText,
    },
    {
      title: 'ai-agent.new-submenu.test',
      icon: 'test',
      isDisabled: true,
      disabledTooltipText,
    },
    {
      title: 'ai-agent.new-submenu.deploy',
      icon: 'rocket-ship',
      isDisabled: true,
      disabledTooltipText,
    },
    {
      title: 'ai-agent.new-submenu.analyze',
      icon: 'column-chart',
      isDisabled: true,
      disabledTooltipText,
    },
  ];
};

const ECOMMERCE_MENU_STRUCTURE = (app: any): MenuItem[] => {
  let showProcedures = app.canUseFinProcedures || !app.canUseAiAgentWorkflows;
  return [
    {
      title: 'ai-agent.new-submenu.train',
      icon: 'article',
      items: [
        {
          title: 'ai-agent.new-submenu.content',
          url: 'apps.app.automation.fin-ai-agent.ecommerce.content',
        },
        {
          title: 'ai-agent.new-submenu.guidance',
          url: 'apps.app.automation.fin-ai-agent.ecommerce.guidance',
          shouldHide: !app.canUseFinGuidance,
        },
        {
          title: showProcedures
            ? 'fin-ai-agent.train.procedures.title'
            : 'operator.sub-menu.fin-ai-agent.tasks',
          url: showProcedures
            ? 'apps.app.automation.fin-ai-agent.ecommerce.procedures.index'
            : 'apps.app.automation.fin-ai-agent.ecommerce.tasks.index',
        },
        {
          title: 'ai-agent.new-submenu.ai-categories',
          url: 'apps.app.automation.fin-ai-agent.ecommerce.attributes',
          shouldHide: !app.canUseFinFirstAiCategories,
        },
      ],
    },
    {
      title: 'ai-agent.new-submenu.deploy',
      icon: 'rocket-ship',
      items: [
        {
          title: 'ai-agent.new-submenu.chat',
          url: 'apps.app.automation.fin-ai-agent.ecommerce.deploy.chat',
        },
      ],
    },
    {
      title: 'ai-agent.new-submenu.analyze',
      icon: 'column-chart',
      items: [
        {
          title: 'ai-agent.new-submenu.performance',
          url: 'apps.app.automation.fin-ai-agent.ecommerce.performance',
        },
        {
          title: 'ai-agent.new-submenu.conversations',
          external: true,
          url: 'inbox.workspace.inbox.fin-view-redirect',
        },
      ],
    },
    {
      title: 'ai-agent.new-submenu.settings',
      url: 'apps.app.automation.fin-ai-agent.settings.index',
      icon: 'settings',
      shouldHide: app.canUseMultiBrandGA,
    },
    {
      title: 'ai-agent.new-submenu.settings',
      icon: 'settings',
      items: [
        {
          title: 'ai-agent.sub-menu.settings.general',
          url: 'apps.app.automation.fin-ai-agent.settings.index',
        },
        {
          title: 'ai-agent.sub-menu.settings.audiences',
          url: 'apps.app.automation.fin-ai-agent.settings.audiences',
        },
      ],
      shouldHide: !app.canUseMultiBrandGA,
    },
    {
      title: '',
      isSeparator: true,
    },
    {
      title: 'ai-agent.new-submenu.workflows',
      icon: 'workflows',
      url: 'apps.app.automation.workflows-overview',
    },
  ];
};

interface Signature {
  profileCount: number;
  customAnswersCount: number;
  contentSuggestionsCount: number;
  gettingStartedState: FinGettingStartedStates;
}

export default class SubmenuNav extends Component<Signature> {
  @service appService: any;
  @service router: any;
  @service declare aiAgentSetupService: AiAgentSetupService;
  @service declare finAgentTypeService: FinAgentTypeService;
  @service declare reactTeammateAppIsland: ReactTeammateAppIslandService;

  private unsubscribeToggle?: () => void;
  private unsubscribeSdrConfigSaved?: () => void;

  // The active section is the one where the page we currently are resides
  @tracked activeSection: any =
    this.menuItems.find((item: any) => this.checkIfSectionIsCurrentRoute(item))?.title || '';
  // we want to be able to open 2 sections, the active one and another one only
  @tracked secondaryOpenSection: any = '';
  @tracked isLoading = true;
  @tracked hasSdrConfig = false;
  @service declare customerService: $TSFixMe;
  @service declare greatGuidanceService: GreatGuidanceService;

  private routeMatchesTarget(currentRoute: string, targetRoute: string): boolean {
    return currentRoute === targetRoute || currentRoute.startsWith(`${targetRoute}.`);
  }

  checkIfSectionIsCurrentRoute(section: any) {
    return (
      this.routeMatchesTarget(this.router.currentRouteName, section.url) ||
      section.items?.any(
        (item: any) =>
          this.router.currentURL
            .split('?')[0]
            .contains?.(this.router.urlFor(item.url, this.appService.app.id)) ||
          this.routeMatchesTarget(this.router.currentRouteName, item.url),
      )
    );
  }

  constructor(owner: any, args: any) {
    super(owner, args);
    this.router.on('routeDidChange', this.onRouteDidChange);
    this.load();

    if (this.showAgentTypeToggle) {
      this.unsubscribeToggle = this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'fin-agent-type:toggle-clicked',
        (data) => {
          let { agentType } = data as { agentType: string };
          if (agentType === 'sales') {
            this.finAgentTypeService.setCurrentAgentType('sales');
            this.router.transitionTo('apps.app.automation.fin-ai-agent.sales');
          } else if (agentType === 'all-roles') {
            this.router.transitionTo('apps.app.automation.fin-ai-agent.all-roles');
          } else if (agentType === 'ecommerce') {
            this.finAgentTypeService.setCurrentAgentType('ecommerce');
            this.router.transitionTo('apps.app.automation.fin-ai-agent.ecommerce');
          } else {
            this.finAgentTypeService.setCurrentAgentType('support');
            this.router.transitionTo('apps.app.automation.fin-ai-agent.setup');
          }
        },
      );

      this.unsubscribeSdrConfigSaved = this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'fin-sdr:config:saved',
        () => {
          this.hasSdrConfig = true;
        },
      );
    }
  }

  async load() {
    this.isLoading = true;
    await Promise.all([this.aiAgentSetupService.load(), this.loadSdrConfig()]);
    this.isLoading = false;
  }

  private async loadSdrConfig() {
    try {
      let appId = this.appService.app.id;
      await get(`/teammate_app/fin_sdr_config?app_id=${appId}`);
      this.hasSdrConfig = true;
    } catch {
      this.hasSdrConfig = false;
    }
  }

  get squaredRightRadius() {
    return (
      (RouteRegexes.automation.contentSuggestions.test(this.router.currentRouteName) &&
        this.appService.app.canSeeSuggestions) ||
      RouteRegexes.automation.optimizeReasons.test(this.router.currentRouteName) ||
      RouteRegexes.automation.optimizeSuggestions.test(this.router.currentRouteName)
    );
  }

  getActiveSection() {
    return this.menuItems.find((item: any) => this.checkIfSectionIsCurrentRoute(item))?.title || '';
  }

  willDestroy() {
    super.willDestroy();
    this.router.off('routeDidChange', this.onRouteDidChange);
    this.unsubscribeToggle?.();
    this.unsubscribeSdrConfigSaved?.();
  }

  @action
  onRouteDidChange() {
    if (this.showAgentTypeToggle) {
      let routeName = this.router.currentRouteName;
      if (routeName?.endsWith('.loading')) {
        return;
      }
      if (routeName?.includes('.sales.')) {
        this.finAgentTypeService.setCurrentAgentType('sales');
      } else if (routeName?.includes('.ecommerce.')) {
        this.finAgentTypeService.setCurrentAgentType('ecommerce');
      } else if (
        routeName?.startsWith(FIN_AI_AGENT_ROUTE_PREFIX) &&
        !SHARED_ROUTE_PREFIXES.some((p: string) => routeName?.startsWith(p))
      ) {
        this.finAgentTypeService.setCurrentAgentType('support');
      }

      this.reactTeammateAppIsland.dispatchTeammateAppEvent('fin-agent-type:changed', {
        agentType: this.currentDropdownAgentType,
      });
    }

    this.activeSection =
      this.menuItems.find((item: any) => this.checkIfSectionIsCurrentRoute(item))?.title || '';
  }

  get showAgentTypeToggle() {
    return this.appService.app.canUseSdrInformationArchitectureChange;
  }

  get currentDropdownAgentType(): string {
    if (this.router.currentRouteName?.startsWith('apps.app.automation.fin-ai-agent.all-roles')) {
      return 'all-roles';
    }
    return this.finAgentTypeService.currentAgentType;
  }

  get showEcommerceOption() {
    return this.appService.app.canUseFinEcommerceAgent;
  }

  get menuItems() {
    if (this.showAgentTypeToggle) {
      if (this.router.currentRouteName?.startsWith('apps.app.automation.fin-ai-agent.all-roles')) {
        return ALL_ROLES_MENU_STRUCTURE();
      }
      if (this.finAgentTypeService.currentAgentType === 'sales') {
        return SALES_MENU_STRUCTURE(
          this.appService.app,
          this.hasSdrConfig,
          this.appService.app.canUseFinSalesAgent,
        );
      }
      if (this.finAgentTypeService.currentAgentType === 'ecommerce') {
        return ECOMMERCE_MENU_STRUCTURE(this.appService.app);
      }
    }

    return MENU_STRUCTURE(
      this.appService.app,
      this.args.profileCount,
      this.args.customAnswersCount,
      this.args.contentSuggestionsCount,
      this.aiAgentSetupService.isFinSetupChatRulesetLive ||
        this.aiAgentSetupService.isFinLiveInChannelOutsideSetup('chat')
        ? 'online'
        : 'offline',
      this.aiAgentSetupService.isFinSetupEmailRulesetLive ||
        this.aiAgentSetupService.isFinLiveInChannelOutsideSetup('email')
        ? 'online'
        : 'offline',
      this.aiAgentSetupService.isFinSetupPhoneRulesetLive ||
        this.aiAgentSetupService.isFinLiveInChannelOutsideSetup('phone')
        ? 'online'
        : 'offline',
      this.aiAgentSetupService.isFinAgentApiLive ? 'online' : 'offline',
      canSeeCustomAnswers(this.appService.app, this.customerService),
      this.args.gettingStartedState,
      this.showAgentTypeToggle,
    );
  }

  @action
  toggleSection(section: any) {
    // If section has both URL and items (like Workflows with debugger enabled)
    if (section.url && section.items) {
      // Navigate to the URL
      this.router.transitionTo(section.url);

      // Also handle submenu toggling
      let currentRouteSection = this.getActiveSection();
      if (currentRouteSection === section.title) {
        this.activeSection = this.activeSection === section.title ? '' : section.title;
      } else {
        this.secondaryOpenSection =
          this.secondaryOpenSection === section.title ? '' : section.title;
      }
      return;
    }

    // If section only has URL (no submenu items)
    if (section.url) {
      this.router.transitionTo(section.url);
      return;
    }

    // If section has no URL (only submenu items)
    let activeSection = this.getActiveSection();
    if (activeSection === section.title) {
      this.activeSection = this.activeSection === section.title ? '' : section.title;
    } else {
      this.secondaryOpenSection = this.secondaryOpenSection === section.title ? '' : section.title;
    }
  }

  @action
  isSectionActive(section: any) {
    let currentRouteName = this.router.currentRouteName;

    if (
      section.url === BATCH_TEST_ROUTE &&
      !this.appService.app.canUseSophisticatedTestingCentralisedPage
    ) {
      // Match for any route that starts with the Batch Test route
      return currentRouteName.startsWith('apps.app.automation.fin-ai-agent.testing.');
    }

    // If section has both URL and items (like Workflows with debugger enabled),
    // don't make the main button active - let the submenu items be active instead
    if (section.url && section.items) {
      return false;
    }

    return this.routeMatchesTarget(currentRouteName, section.url);
  }

  @action
  isSectionOpen(section: any) {
    return this.activeSection === section.title || this.secondaryOpenSection === section.title;
  }

  @action
  isSubmenuItemActive(item: any) {
    let currentRouteName = this.router.currentRouteName;

    // Special handling for testing routes
    if (item.url === BATCH_TEST_ROUTE) {
      return currentRouteName.startsWith('apps.app.automation.fin-ai-agent.testing.');
    }

    if (item.url === SIMULATIONS_ROUTE) {
      return currentRouteName.startsWith('apps.app.automation.fin-ai-agent.simulations');
    }

    return (
      this.routeMatchesTarget(currentRouteName, item.url) ||
      this.router.currentURL
        .split('?')[0]
        .contains?.(this.router.urlFor(item.url, this.appService.app.id))
    );
  }

  get isWidgetVisible() {
    return !this.greatGuidanceService.hideFloatingWidget;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAutomation::SubmenuNav': typeof SubmenuNav;
    'ai-automation/submenu-nav': typeof SubmenuNav;
  }
}
